import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ChevronLeft, ChevronRight, MapPin, Upload, Zap, Droplets, ImageIcon,
  FileCheck, Instagram, Phone, Mail, User, LayoutGrid, Truck,
  CheckCircle2, Save, Globe, Users, TrendingUp,
  Video, CalendarDays
} from 'lucide-react';
import { motion, AnimatePresence } from 'motion/react';
import { useAuth } from '../contexts/AuthContext';
import { db } from '../lib/firebase';
import { doc, getDoc, setDoc, serverTimestamp, deleteField, deleteDoc } from 'firebase/firestore';
import { toast } from 'react-hot-toast';
import { DocumentDropzone } from '../components/DocumentDropzone';
import { computeStandCompletion } from '../utils/profileCompletion';
import { MUSIC_STYLES } from '../utils/musicStyles';

// --- TYPES ---
interface FormData {
  // Étape 1 — Identité & storytelling
  name: string;
  logo: string;
  bannerUrl: string;
  brandStory: string;
  instagram: string;
  tiktok: string;
  foundingYear: number;
  contactPerson: string;
  email: string;
  phone: string;
  // Étape 2 — Galerie & vidéo
  galleryUrls?: string[];
  videoUrl?: string;
  pressKitUrl?: string;
  // Étape 3 — Expérience festival
  experienceRange: string;
  pastFestivals: string;
  staffNames: string;
  // Étape 4 — CA & potentiel commercial
  revenueRange: string;
  maxDailyVolume: string;
  // Étape 5 — Activité & specs
  standType: string;
  region: string;
  musicStyles: string[]; // styles musicaux préférés — critère « thème » du matching
  description: string;
  priceRange: string;
  width: number;
  depth: number;
  needsElectricity: boolean;
  powerKw: number;
  waterNeeds: boolean;
  hasVehicle: boolean;
  vehicleDetails: string;
  staffBadges: number;
  furnitureNeeds: string;
  // Étape 6 — Documents légaux
  insuranceUrl?: string;
  kbisUrl?: string;
  sanitaryUrl?: string;
  // Meta
  isDraft: boolean;
}

const INITIAL_DATA: FormData = {
  name: '',
  logo: '',
  bannerUrl: '',
  brandStory: '',
  instagram: '',
  tiktok: '',
  foundingYear: 0,
  contactPerson: '',
  email: '',
  phone: '',
  galleryUrls: [],
  videoUrl: '',
  pressKitUrl: '',
  experienceRange: '',
  pastFestivals: '',
  staffNames: '',
  revenueRange: '',
  maxDailyVolume: '',
  standType: '',
  region: '',
  musicStyles: [],
  description: '',
  priceRange: '',
  width: 0,
  depth: 0,
  needsElectricity: false,
  powerKw: 0,
  waterNeeds: false,
  hasVehicle: false,
  vehicleDetails: '',
  staffBadges: 0,
  furnitureNeeds: '',
  insuranceUrl: '',
  kbisUrl: '',
  sanitaryUrl: '',
  isDraft: true,
};

const STEP_LABELS = ['Identité', 'Activité', 'Galerie', 'Potentiel'];

// --- FIELD BADGES ---
export const C = () => (
  <span className="inline-flex items-center text-[9px] font-black uppercase tracking-widest text-[#ff5a00] bg-[#ff5a00]/10 border border-[#ff5a00]/20 px-1.5 py-0.5 rounded-full ml-1.5 align-middle">
    Obligatoire
  </span>
);
export const R = () => (
  <span className="inline-flex items-center text-[9px] font-black uppercase tracking-widest text-blue-400 bg-blue-400/10 border border-blue-400/20 px-1.5 py-0.5 rounded-full ml-1.5 align-middle">
    +Score
  </span>
);

// --- COMPLETION BAR ---
const CompletionBar = ({
  currentStep,
  totalSteps,
  formData,
}: {
  currentStep: number;
  totalSteps: number;
  formData: Record<string, any>;
}) => {
  const { criticalPct, recommendedPct, overallPct, criticalFilled, criticalTotal, recommendedFilled, recommendedTotal } =
    computeStandCompletion(formData);

  const color =
    overallPct >= 80 ? 'text-emerald-400' : overallPct >= 50 ? 'text-yellow-400' : 'text-gray-400';

  return (
    <div className="mb-8">
      {/* Step dots */}
      <div className="flex items-center space-x-2 mb-4">
        {Array.from({ length: totalSteps }).map((_, i) => (
          <div
            key={i}
            className={`h-1.5 flex-1 rounded-full transition-all duration-500 ${
              i + 1 <= currentStep ? 'bg-[#ff5a00] shadow-[0_0_10px_rgba(255,92,0,0.5)]' : 'bg-white/10'
            }`}
          />
        ))}
      </div>

      {/* Score row */}
      <div className="flex items-center justify-between mb-1.5">
        <div className="flex items-center gap-4 text-[11px]">
          <div className="flex items-center gap-1.5">
            <div className="w-2 h-2 rounded-full bg-[#ff5a00]" />
            <span className="text-gray-500 font-bold">
              Obligatoire <span className="text-white">{criticalFilled}/{criticalTotal}</span>
            </span>
          </div>
          <div className="flex items-center gap-1.5">
            <div className="w-2 h-2 rounded-full bg-blue-400" />
            <span className="text-gray-500 font-bold">
              Score <span className="text-white">{recommendedFilled}/{recommendedTotal}</span>
            </span>
          </div>
        </div>
        <span className={`text-sm font-black tabular-nums ${color}`}>{overallPct}%</span>
      </div>

      {/* Two-segment bar */}
      <div className="h-1.5 bg-white/5 rounded-full overflow-hidden flex">
        <div
          className="h-full bg-[#ff5a00] transition-all duration-700 ease-out"
          style={{ width: `${criticalPct * 0.7}%` }}
        />
        <div
          className="h-full bg-blue-400/60 transition-all duration-700 ease-out"
          style={{ width: `${recommendedPct * 0.3}%` }}
        />
      </div>
    </div>
  );
};

// --- SECTION HEADER ---
const SectionHeader = ({
  icon,
  title,
  subtitle,
  color = 'orange',
}: {
  icon: React.ReactNode;
  title: string;
  subtitle: string;
  color?: 'orange' | 'purple' | 'yellow' | 'emerald' | 'blue' | 'indigo';
}) => {
  const colors: Record<string, string> = {
    orange:  'bg-[#ff5a00]/20 border-[#ff5a00]/30 text-[#ff5a00]',
    purple:  'bg-purple-500/20 border-purple-500/30 text-purple-400',
    yellow:  'bg-yellow-500/20 border-yellow-500/30 text-yellow-400',
    emerald: 'bg-emerald-500/20 border-emerald-500/30 text-emerald-400',
    blue:    'bg-blue-500/20 border-blue-500/30 text-blue-400',
    indigo:  'bg-indigo-500/20 border-indigo-500/30 text-indigo-400',
  };
  return (
    <div className="flex items-center space-x-4 mb-10">
      <div className={`w-12 h-12 rounded-2xl flex items-center justify-center border ${colors[color]}`}>
        {icon}
      </div>
      <div>
        <h2 className="text-2xl font-bold">{title}</h2>
        <p className="text-sm text-gray-400">{subtitle}</p>
      </div>
    </div>
  );
};

// --- TOGGLE BUTTON ---
const ToggleButton = ({
  active, onClick, icon, label, activeClass,
}: {
  active: boolean; onClick: () => void; icon: React.ReactNode; label: string; activeClass: string;
}) => (
  <button
    onClick={onClick}
    className={`w-full py-4 rounded-2xl border font-bold transition-all flex items-center justify-center ${
      active ? activeClass : 'bg-white/5 border-white/5 text-gray-500 hover:bg-white/10'
    }`}
  >
    <span className="mr-3">{icon}</span>
    {label}
  </button>
);

// ============================================================
export default function MyStandPage() {
  const navigate = useNavigate();
  const { user, loading } = useAuth();

  const [step, setStep] = useState(1);
  const totalSteps = 4;
  const [formData, setFormData] = useState<FormData>(INITIAL_DATA);
  const [isSaving, setIsSaving] = useState(false);
  const [hasLoaded, setHasLoaded] = useState(false);
  const logoInputRef = useRef<HTMLInputElement>(null);
  const bannerInputRef = useRef<HTMLInputElement>(null);

  // --- FETCH EXISTING DATA ---
  useEffect(() => {
    if (!loading && !user) { navigate('/'); return; }

    const fetchStand = async () => {
      if (!user) return;
      try {
        const docRef = doc(db, 'stands', user.uid);
        const docSnap = await getDoc(docRef);

        let priv: any = {};
        try {
          const privSnap = await getDoc(doc(db, 'stands', user.uid, 'private', 'documents'));
          priv = privSnap.exists() ? (privSnap.data() as any) : {};
        } catch (privErr) {
          console.warn('Documents privés illisibles', privErr);
        }

        if (docSnap.exists()) {
          const data = docSnap.data() as FormData;
          setFormData({
            ...INITIAL_DATA,
            ...data,
            insuranceUrl: priv.insuranceUrl ?? data.insuranceUrl ?? '',
            kbisUrl: priv.kbisUrl ?? data.kbisUrl ?? '',
            sanitaryUrl: priv.sanitaryUrl ?? data.sanitaryUrl ?? '',
          });
        }
      } catch (error) {
        console.error('Error fetching stand', error);
      } finally {
        setHasLoaded(true);
      }
    };

    if (user) fetchStand();
  }, [user, loading, navigate]);

  // --- HANDLERS ---
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value, type } = e.target;
    let val: any = value;
    if (type === 'number') {
      val = value === '' ? 0 : parseFloat(value);
      if (isNaN(val)) val = 0;
    }
    setFormData(prev => ({ ...prev, [name]: val }));
  };

  const toggleBoolean = (name: keyof FormData) => {
    setFormData(prev => ({ ...prev, [name]: !prev[name] }));
  };

  // --- IMAGE COMPRESSION ---
  const compressImage = (file: File, maxSize = 400, quality = 0.8): Promise<string> =>
    new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onerror = reject;
      reader.onload = (e) => {
        const img = new Image();
        img.onerror = reject;
        img.onload = () => {
          let w = img.width, h = img.height;
          if (w > h) { if (w > maxSize) { h = Math.round(h * maxSize / w); w = maxSize; } }
          else { if (h > maxSize) { w = Math.round(w * maxSize / h); h = maxSize; } }
          const canvas = document.createElement('canvas');
          canvas.width = w; canvas.height = h;
          const ctx = canvas.getContext('2d');
          if (!ctx) { reject(new Error('Canvas not supported')); return; }
          ctx.drawImage(img, 0, 0, w, h);
          resolve(canvas.toDataURL('image/jpeg', quality));
        };
        img.src = e.target?.result as string;
      };
      reader.readAsDataURL(file);
    });

  const handleLogoUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!e.target.files?.length) return;
    const file = e.target.files[0];
    if (!file.type.startsWith('image/')) { toast.error('Format non supporté. Utilise PNG ou JPG.'); return; }
    try {
      const compressed = await compressImage(file, 400, 0.8);
      setFormData(prev => ({ ...prev, logo: compressed }));
      toast.success('Logo chargé !');
    } catch { toast.error("Erreur lors du chargement de l'image."); }
  };

  const handleBannerUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!e.target.files?.length) return;
    const file = e.target.files[0];
    if (!file.type.startsWith('image/')) { toast.error('Format non supporté. Utilise PNG ou JPG.'); return; }
    try {
      const compressed = await compressImage(file, 1200, 0.85);
      setFormData(prev => ({ ...prev, bannerUrl: compressed }));
      toast.success('Bannière chargée !');
    } catch { toast.error("Erreur lors du chargement de la bannière."); }
  };

  // --- SAVE ---
  const saveToFirebase = async (isDraft: boolean) => {
    if (!user) return;
    setIsSaving(true);
    try {
      const legalDocs = {
        insuranceUrl: formData.insuranceUrl || '',
        kbisUrl: formData.kbisUrl || '',
        sanitaryUrl: formData.sanitaryUrl || '',
      };

      const standData: any = {
        ...formData,
        isDraft,
        ownerId: user.uid,
        updatedAt: serverTimestamp(),
        insuranceUrl: deleteField(),
        kbisUrl: deleteField(),
        sanitaryUrl: deleteField(),
      };

      if ((formData as any).createdAt?.nanoseconds !== undefined) {
        standData.createdAt = (formData as any).createdAt;
      } else {
        standData.createdAt = serverTimestamp();
      }

      try {
        await setDoc(doc(db, 'stands', user.uid), standData, { merge: true });
      } catch (err: any) {
        console.error('[saveStand] Échec doc principal :', err?.code, err?.message);
        toast.error(`Erreur sauvegarde (${err?.code ?? 'inconnu'})`);
        return;
      }

      try {
        await setDoc(
          doc(db, 'stands', user.uid, 'private', 'documents'),
          { ...legalDocs, updatedAt: serverTimestamp() },
          { merge: true }
        );
      } catch (err: any) {
        console.warn('Documents privés non sauvegardés', err?.code);
      }

      // --- Email reminder logic ---
      const { overallPct } = computeStandCompletion({ ...formData, ...legalDocs });
      if (!isDraft) {
        // Profile published — cancel any pending reminder
        try { await deleteDoc(doc(db, 'profileReminders', user.uid)); } catch {}
        toast.success('Profil finalisé avec succès !');
        navigate('/stand/profile');
      } else {
        // Draft — schedule reminder if profile is incomplete
        if (overallPct < 80 && user.email) {
          const scheduledFor = new Date(Date.now() + 24 * 60 * 60 * 1000);
          try {
            await setDoc(doc(db, 'profileReminders', user.uid), {
              email: user.email,
              standName: formData.name || 'votre stand',
              completionPct: overallPct,
              scheduledFor,
              sent: false,
              updatedAt: serverTimestamp(),
            }, { merge: true });
          } catch {}
        }
        toast.success('Brouillon sauvegardé !');
      }
    } catch (error: any) {
      console.error('[saveStand] Erreur inattendue :', error);
      toast.error('Erreur inattendue lors de la sauvegarde.');
    } finally {
      setIsSaving(false);
    }
  };

  // --- VALIDATION ---
  const validateStep = (s: number) => {
    if (s === 1) return formData.name.length > 2 && formData.contactPerson.length > 2;
    if (s === 2) return formData.standType !== '' && formData.region !== '';
    return true;
  };

  const nextStep = () => {
    if (validateStep(step)) { setStep(prev => prev + 1); window.scrollTo(0, 0); }
    else toast.error('Veuillez remplir correctement les champs obligatoires.');
  };

  const prevStep = () => { setStep(prev => prev - 1); window.scrollTo(0, 0); };

  if (!hasLoaded) return (
    <div className="min-h-screen bg-black flex items-center justify-center">
      <div className="w-12 h-12 border-4 border-[#ff5a00]/30 border-t-[#ff5a00] rounded-full animate-spin" />
    </div>
  );

  // --- NAV BUTTONS ---
  const NavButtons = ({ isLast = false }: { isLast?: boolean }) => (
    <div className="mt-12 flex justify-between">
      {step > 1 ? (
        <button onClick={prevStep} className="hover:bg-white/5 text-gray-500 hover:text-white font-bold py-4 px-8 rounded-2xl flex items-center transition-all">
          <ChevronLeft className="mr-2 w-5 h-5" /> Retour
        </button>
      ) : <div />}
      {isLast ? (
        <button
          onClick={() => saveToFirebase(false)}
          disabled={isSaving}
          className="bg-emerald-500 hover:bg-emerald-600 text-white font-black py-4 px-10 rounded-2xl flex items-center transition-all shadow-lg shadow-emerald-500/20 active:scale-95 disabled:opacity-50"
        >
          {isSaving ? 'Publication...' : 'Finaliser le Profil'} <CheckCircle2 className="ml-2 w-5 h-5" />
        </button>
      ) : (
        <button onClick={nextStep} className="bg-[#ff5a00] hover:bg-[#ff6b1a] text-white font-black py-4 px-10 rounded-2xl flex items-center transition-all shadow-lg shadow-[#ff5a00]/20 active:scale-95">
          Suivant <ChevronRight className="ml-2 w-5 h-5" />
        </button>
      )}
    </div>
  );

  return (
    <div className="min-h-screen bg-black text-white font-sans pb-24">
      <div className="max-w-4xl mx-auto px-6 pt-12">

        {/* Header */}
        <div className="flex items-center justify-between mb-2">
          <h1 className="text-3xl font-black tracking-tight">Profil Exposant</h1>
          <button
            onClick={() => saveToFirebase(true)}
            disabled={isSaving}
            className="flex items-center text-sm font-bold text-gray-400 hover:text-white transition-colors"
          >
            <Save className="w-4 h-4 mr-2" />
            Sauvegarder en brouillon
          </button>
        </div>
        <p className="text-gray-500 mb-4 font-medium">
          Étape {step} sur {totalSteps} • {STEP_LABELS[step - 1]}
        </p>

        <CompletionBar currentStep={step} totalSteps={totalSteps} formData={formData as Record<string, any>} />

        <div className="bg-[#121212] rounded-[2.5rem] border border-white/5 overflow-hidden shadow-2xl shadow-black">
          <AnimatePresence mode="wait">

            {/* ===== ÉTAPE 1 — IDENTITÉ & STORYTELLING ===== */}
            {step === 1 && (
              <motion.div key="step1" initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -20 }} className="p-8 md:p-12">
                <SectionHeader
                  icon={<User className="w-6 h-6" />}
                  title="Identité & Storytelling"
                  subtitle="Racontez votre marque aux organisateurs"
                  color="orange"
                />

                <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                  {/* Nom commercial — en premier : c'est la donnée la plus structurante */}
                  <div className="md:col-span-2">
                    <label className="block text-sm font-bold text-gray-400 mb-2">Nom Commercial <C /></label>
                    <input type="text" name="name" value={formData.name} onChange={handleInputChange}
                      placeholder="Ex: Festin Gourmet"
                      className="w-full bg-white/5 border border-white/10 rounded-2xl px-5 py-4 focus:outline-none focus:ring-2 focus:ring-[#ff5a00] transition-all placeholder:text-gray-600 font-medium" />
                  </div>

                  {/* Histoire de la marque */}
                  <div className="md:col-span-2">
                    <label className="block text-sm font-bold text-gray-400 mb-2">
                      Histoire de la marque <R />
                      <span className="text-gray-500 font-normal text-xs ml-2">ce qui vous différencie</span>
                    </label>
                    <textarea name="brandStory" value={formData.brandStory} onChange={handleInputChange} rows={4}
                      placeholder="Pourquoi cette marque existe ? Quelle est votre signature, votre univers ? Ce texte est lu en premier par les organisateurs avant de vous sélectionner..."
                      className="w-full bg-white/5 border border-white/10 rounded-2xl px-5 py-4 focus:outline-none focus:ring-2 focus:ring-[#ff5a00] transition-all placeholder:text-gray-600 font-medium resize-none" />
                  </div>

                  {/* Réseaux sociaux */}
                  <div>
                    <label className="block text-sm font-bold text-gray-400 mb-2">Instagram <R /></label>
                    <div className="relative">
                      <Instagram className="absolute left-5 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-500" />
                      <input type="text" name="instagram" value={formData.instagram} onChange={handleInputChange}
                        placeholder="@votre_stand"
                        className="w-full bg-white/5 border border-white/10 rounded-2xl pl-14 pr-5 py-4 focus:outline-none focus:ring-2 focus:ring-[#ff5a00] transition-all placeholder:text-gray-600 font-medium" />
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-bold text-gray-400 mb-2">TikTok</label>
                    <div className="relative">
                      <span className="absolute left-5 top-1/2 -translate-y-1/2 text-gray-500 font-black text-xs">TT</span>
                      <input type="text" name="tiktok" value={formData.tiktok} onChange={handleInputChange}
                        placeholder="@votre_tiktok"
                        className="w-full bg-white/5 border border-white/10 rounded-2xl pl-14 pr-5 py-4 focus:outline-none focus:ring-2 focus:ring-[#ff5a00] transition-all placeholder:text-gray-600 font-medium" />
                    </div>
                  </div>

                  {/* Année de création */}
                  <div>
                    <label className="block text-sm font-bold text-gray-400 mb-2">Année de création <R /></label>
                    <div className="relative">
                      <CalendarDays className="absolute left-5 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-500" />
                      <input type="number" name="foundingYear" value={formData.foundingYear || ''} onChange={handleInputChange}
                        placeholder="Ex: 2019" min="1980" max={new Date().getFullYear()}
                        className="w-full bg-white/5 border border-white/10 rounded-2xl pl-14 pr-5 py-4 focus:outline-none focus:ring-2 focus:ring-[#ff5a00] transition-all placeholder:text-gray-600 font-medium" />
                    </div>
                  </div>

                  {/* Responsable */}
                  <div>
                    <label className="block text-sm font-bold text-gray-400 mb-2">Responsable <C /></label>
                    <div className="relative">
                      <User className="absolute left-5 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-500" />
                      <input type="text" name="contactPerson" value={formData.contactPerson} onChange={handleInputChange}
                        placeholder="Nom & Prénom"
                        className="w-full bg-white/5 border border-white/10 rounded-2xl pl-14 pr-5 py-4 focus:outline-none focus:ring-2 focus:ring-[#ff5a00] transition-all placeholder:text-gray-600 font-medium" />
                    </div>
                  </div>

                  {/* Email */}
                  <div>
                    <label className="block text-sm font-bold text-gray-400 mb-2">Email Contact</label>
                    <div className="relative">
                      <Mail className="absolute left-5 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-500" />
                      <input type="email" name="email" value={formData.email} onChange={handleInputChange}
                        placeholder="contact@votre-stand.com"
                        className="w-full bg-white/5 border border-white/10 rounded-2xl pl-14 pr-5 py-4 focus:outline-none focus:ring-2 focus:ring-[#ff5a00] transition-all placeholder:text-gray-600 font-medium" />
                    </div>
                  </div>

                  {/* Téléphone */}
                  <div>
                    <label className="block text-sm font-bold text-gray-400 mb-2">Téléphone</label>
                    <div className="relative">
                      <Phone className="absolute left-5 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-500" />
                      <input type="tel" name="phone" value={formData.phone} onChange={handleInputChange}
                        placeholder="06 12 34 56 78"
                        className="w-full bg-white/5 border border-white/10 rounded-2xl pl-14 pr-5 py-4 focus:outline-none focus:ring-2 focus:ring-[#ff5a00] transition-all placeholder:text-gray-600 font-medium" />
                    </div>
                  </div>

                  {/* Logo — en dernier : après le nom et l'histoire, une fois l'exposant engagé dans le texte */}
                  <div className="md:col-span-2">
                    <label className="block text-sm font-bold text-gray-400 mb-4">Logo ou Photo de Profil</label>
                    <div className="flex items-center space-x-6">
                      <div className="w-24 h-24 rounded-3xl bg-white/5 border border-white/10 flex items-center justify-center overflow-hidden shrink-0">
                        {formData.logo
                          ? <img src={formData.logo} alt="Logo" className="w-full h-full object-cover" />
                          : <ImageIcon className="w-8 h-8 text-gray-600" />}
                      </div>
                      <div
                        className="flex-1 flex flex-col items-center justify-center p-6 border-2 border-dashed border-white/10 rounded-3xl hover:border-[#ff5a00]/50 hover:bg-[#ff5a00]/5 transition-all cursor-pointer group"
                        onClick={() => logoInputRef.current?.click()}
                      >
                        <Upload className="w-5 h-5 text-gray-400 group-hover:text-[#ff5a00] mb-2" />
                        <span className="text-sm font-bold">Cliquer pour uploader</span>
                        <span className="text-xs text-gray-500 mt-1">400 × 400 px · PNG, JPG</span>
                        <input ref={logoInputRef} type="file" accept="image/*" onChange={handleLogoUpload} style={{ position: 'absolute', opacity: 0, width: 0, height: 0 }} />
                      </div>
                    </div>
                  </div>

                  {/* Bannière */}
                  <div className="md:col-span-2">
                    <label className="block text-sm font-bold text-gray-400 mb-4">Photo de Couverture <span className="text-gray-600 font-normal">(bannière)</span></label>
                    <div
                      className="relative w-full h-36 rounded-3xl border-2 border-dashed border-white/10 hover:border-[#ff5a00]/50 hover:bg-[#ff5a00]/5 transition-all cursor-pointer overflow-hidden group"
                      onClick={() => bannerInputRef.current?.click()}
                    >
                      {formData.bannerUrl ? (
                        <>
                          <img src={formData.bannerUrl} alt="Bannière" className="w-full h-full object-cover" />
                          <div className="absolute inset-0 bg-black/40 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                            <Upload className="w-6 h-6 text-white mr-2" /><span className="text-sm font-bold text-white">Changer</span>
                          </div>
                        </>
                      ) : (
                        <div className="flex flex-col items-center justify-center h-full">
                          <Upload className="w-6 h-6 text-gray-400 group-hover:text-[#ff5a00] mb-2" />
                          <span className="text-sm font-bold">Ajouter une photo de couverture</span>
                          <span className="text-xs text-gray-500 mt-1">1200 × 400 px · PNG, JPG</span>
                        </div>
                      )}
                      <input ref={bannerInputRef} type="file" accept="image/*" onChange={handleBannerUpload} style={{ position: 'absolute', opacity: 0, width: 0, height: 0 }} />
                    </div>
                  </div>
                </div>

                <NavButtons />
              </motion.div>
            )}

            {/* ===== ÉTAPE 3 — GALERIE & VIDÉO ===== */}
            {step === 3 && (
              <motion.div key="step3" initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -20 }} className="p-8 md:p-12">
                <SectionHeader
                  icon={<ImageIcon className="w-6 h-6" />}
                  title="Galerie & Vidéo"
                  subtitle="Montrez votre univers pour séduire les festivals"
                  color="purple"
                />

                <div className="space-y-10">
                  {/* Galerie photos */}
                  <div>
                    <label className="block text-sm font-bold text-gray-400 mb-4">Galerie Photos <C /> <span className="text-gray-500 font-normal text-xs ml-1">1 photo min · stand, produits, équipe</span></label>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                      {formData.galleryUrls?.map((url, i) => (
                        <div key={i} className="relative aspect-square rounded-2xl bg-white/5 border border-white/10 overflow-hidden group">
                          <img src={url} alt={`Gallery ${i}`} className="w-full h-full object-cover" />
                          <button
                            onClick={() => setFormData(p => ({ ...p, galleryUrls: p.galleryUrls?.filter((_, idx) => idx !== i) }))}
                            className="absolute top-2 right-2 w-8 h-8 bg-black/60 backdrop-blur-md rounded-full flex items-center justify-center text-white opacity-0 group-hover:opacity-100 transition-opacity"
                          >×</button>
                        </div>
                      ))}
                      {(formData.galleryUrls?.length || 0) < 8 && (
                        <DocumentDropzone
                          label="+"
                          storagePath={`stands/${user?.uid}/gallery_${Date.now()}`}
                          onUploadComplete={(url) => setFormData(p => ({ ...p, galleryUrls: [...(p.galleryUrls || []), url] }))}
                          accept="image/*"
                        />
                      )}
                    </div>
                    <p className="text-[10px] text-gray-600 mt-2 uppercase tracking-widest font-black">Jusqu'à 8 photos · JPG/PNG</p>
                  </div>

                  {/* Vidéo de présentation */}
                  <div className="bg-white/5 rounded-[2rem] p-8 border border-white/5">
                    <div className="flex items-center mb-2">
                      <Video className="w-5 h-5 text-purple-400 mr-2" />
                      <h3 className="font-bold text-purple-400">Vidéo de présentation</h3>
                      <span className="ml-3 text-[10px] font-black uppercase tracking-widest bg-[#ff5a00]/20 text-[#ff5a00] px-2 py-0.5 rounded-full">levier n°1</span>
                    </div>
                    <p className="text-xs text-gray-500 mb-6">20–30 sec suffisent. C'est le facteur de conversion le plus important auprès des organisateurs.</p>
                    {formData.videoUrl ? (
                      <div className="space-y-3">
                        <video src={formData.videoUrl} controls className="w-full rounded-2xl max-h-48 bg-black" />
                        <button
                          onClick={() => setFormData(p => ({ ...p, videoUrl: '' }))}
                          className="text-xs text-red-400 hover:text-red-300 font-bold transition-colors"
                        >Supprimer la vidéo</button>
                      </div>
                    ) : (
                      <DocumentDropzone
                        label="Uploader une vidéo (MP4, MOV, WEBM)"
                        storagePath={`stands/${user?.uid}/video_presentation`}
                        onUploadComplete={(url) => setFormData(p => ({ ...p, videoUrl: url }))}
                        accept="video/mp4,video/quicktime,video/webm"
                      />
                    )}
                  </div>

                  {/* Press kit */}
                  <div className="bg-white/5 rounded-[2rem] p-8 border border-white/5">
                    <div className="flex items-center mb-6">
                      <Globe className="w-5 h-5 text-indigo-400 mr-2" />
                      <h3 className="font-bold text-indigo-400">Dossier de Présentation</h3>
                      <span className="ml-2 text-gray-500 font-normal text-sm">optionnel</span>
                    </div>
                    <DocumentDropzone
                      label="Press Kit / Menu PDF / Présentation"
                      storagePath={`stands/${user?.uid}/press_kit`}
                      existingUrl={formData.pressKitUrl}
                      onUploadComplete={(url) => setFormData(p => ({ ...p, pressKitUrl: url }))}
                      accept="application/pdf,image/*"
                    />
                    <p className="text-xs text-gray-500 mt-4 italic">Un dossier complet aide les organisateurs à cerner votre univers rapidement.</p>
                  </div>
                </div>

                <NavButtons />
              </motion.div>
            )}

            {/* ===== ÉTAPE 4 — CA & POTENTIEL COMMERCIAL (dernière étape) ===== */}
            {step === 4 && (
              <motion.div key="step4" initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -20 }} className="p-8 md:p-12">
                <SectionHeader
                  icon={<TrendingUp className="w-6 h-6" />}
                  title="CA & Potentiel Commercial"
                  subtitle="Votre argument de vente auprès des organisateurs"
                  color="emerald"
                />

                <div className="space-y-10">
                  {/* Callout */}
                  <div className="bg-emerald-500/5 border border-emerald-500/20 rounded-2xl p-6 flex items-start space-x-4">
                    <TrendingUp className="w-5 h-5 text-emerald-400 shrink-0 mt-0.5" />
                    <p className="text-sm text-gray-300 leading-relaxed">
                      Un organisateur choisit un stand qui apporte de l'animation <strong>et du chiffre au village</strong>, pas seulement un stand qui coche une catégorie. Ces informations sont déclaratives et confidentielles.
                    </p>
                  </div>

                  {/* CA moyen */}
                  <div>
                    <label className="block text-sm font-bold text-gray-400 mb-4">CA moyen réalisé par festival <R /></label>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                      {[
                        { id: '<500',      label: '< 500 €/j' },
                        { id: '500-1500',  label: '500 – 1 500 €/j' },
                        { id: '1500-5000', label: '1 500 – 5 000 €/j' },
                        { id: '5000+',     label: '> 5 000 €/j' },
                      ].map((range) => (
                        <button key={range.id} type="button"
                          onClick={() => setFormData(p => ({ ...p, revenueRange: p.revenueRange === range.id ? '' : range.id }))}
                          className={`py-4 px-3 rounded-2xl border text-[11px] font-black uppercase tracking-widest transition-all ${
                            formData.revenueRange === range.id
                              ? 'bg-emerald-500 border-emerald-500 text-white shadow-lg shadow-emerald-500/20'
                              : 'bg-white/5 border-white/10 text-gray-400 hover:border-white/20'
                          }`}
                        >{range.label}</button>
                      ))}
                    </div>
                  </div>

                  {/* Volume max — QCM (au lieu d'un texte libre non structuré,
                      difficile à exploiter côté matching/tri) */}
                  <div>
                    <label className="block text-sm font-bold text-gray-400 mb-4">
                      Capacité de production / volume de vente max par jour <R />
                      <span className="block text-xs text-gray-600 font-normal mt-1">Repas, pièces, séances… peu importe l'unité, en volume par jour</span>
                    </label>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                      {[
                        { id: '<50',     label: '< 50 unités/j' },
                        { id: '50-150',  label: '50 – 150 unités/j' },
                        { id: '150-400', label: '150 – 400 unités/j' },
                        { id: '400+',    label: '> 400 unités/j' },
                      ].map((range) => (
                        <button key={range.id} type="button"
                          onClick={() => setFormData(p => ({ ...p, maxDailyVolume: p.maxDailyVolume === range.id ? '' : range.id }))}
                          className={`py-4 px-3 rounded-2xl border text-[11px] font-black uppercase tracking-widest transition-all ${
                            formData.maxDailyVolume === range.id
                              ? 'bg-emerald-500 border-emerald-500 text-white shadow-lg shadow-emerald-500/20'
                              : 'bg-white/5 border-white/10 text-gray-400 hover:border-white/20'
                          }`}
                        >{range.label}</button>
                      ))}
                    </div>
                  </div>

                  {/* Documents légaux — optionnels, en toute fin de parcours.
                      Retirés comme étape bloquante dédiée : la plupart des exposants
                      n'ont pas leurs documents sous la main à l'inscription. Ils
                      restent modifiables ici ou plus tard depuis "Modifier mon profil". */}
                  <div className="pt-4 border-t border-white/5">
                    <div className="flex items-center gap-2 mb-1">
                      <FileCheck className="w-5 h-5 text-emerald-400" />
                      <h3 className="font-bold text-emerald-400">Documents légaux <span className="text-gray-600 font-normal text-xs normal-case">(optionnel)</span></h3>
                    </div>
                    <p className="text-xs text-gray-500 mb-6">Pas sous la main ? Aucun souci, vous pourrez les ajouter plus tard depuis votre profil.</p>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                      <DocumentDropzone
                        label="Assurance RC Pro"
                        storagePath={`stands/${user?.uid}/doc_assurance`}
                        existingUrl={formData.insuranceUrl}
                        onUploadComplete={(url) => setFormData(p => ({ ...p, insuranceUrl: url }))}
                      />
                      <DocumentDropzone
                        label="Kbis / SIRET"
                        storagePath={`stands/${user?.uid}/doc_kbis`}
                        existingUrl={formData.kbisUrl}
                        onUploadComplete={(url) => setFormData(p => ({ ...p, kbisUrl: url }))}
                      />
                      <DocumentDropzone
                        label="Agréments Sanitaires · +Score"
                        storagePath={`stands/${user?.uid}/doc_sanitaire`}
                        existingUrl={formData.sanitaryUrl}
                        onUploadComplete={(url) => setFormData(p => ({ ...p, sanitaryUrl: url }))}
                      />
                    </div>
                    <p className="text-xs text-gray-500 text-center italic mt-6">
                      Ces documents sont stockés de manière sécurisée et ne sont accessibles qu'aux organisateurs qui vous sélectionnent.
                    </p>
                  </div>

                  <div className="bg-[#ff5a00]/5 border border-[#ff5a00]/20 rounded-2xl p-6 flex items-start space-x-4">
                    <CheckCircle2 className="w-6 h-6 text-emerald-500 shrink-0 mt-0.5" />
                    <div>
                      <p className="text-sm font-bold text-white mb-1">Prêt pour la publication !</p>
                      <p className="text-xs text-gray-400 leading-relaxed">
                        Votre profil sera visible par tous les organisateurs. Vous pourrez revenir modifier vos informations à tout moment.
                      </p>
                    </div>
                  </div>
                </div>

                <NavButtons isLast />
              </motion.div>
            )}

            {/* ===== ÉTAPE 2 — ACTIVITÉ & SPECS TECHNIQUES ===== */}
            {step === 2 && (
              <motion.div key="step2" initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -20 }} className="p-8 md:p-12">
                <SectionHeader
                  icon={<Zap className="w-6 h-6" />}
                  title="Activité & Specs Techniques"
                  subtitle="Catégorie, expérience, besoins logistiques et espace"
                  color="yellow"
                />

                <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                  {/* Type de stand */}
                  <div className="md:col-span-2">
                    <label className="block text-sm font-bold text-gray-400 mb-2">Type de Stand <C /></label>
                    <div className="relative">
                      <LayoutGrid className="absolute left-5 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-500 pointer-events-none" />
                      <select name="standType" value={formData.standType} onChange={handleInputChange}
                        className="w-full bg-white/5 border border-white/10 rounded-2xl pl-14 pr-10 py-4 focus:outline-none focus:ring-2 focus:ring-[#ff5a00] transition-all font-medium appearance-none">
                        <option value="" disabled className="bg-[#121212]">Sélectionnez une catégorie</option>
                        <option value="food"        className="bg-[#121212]">Food Truck / Restauration Chaude</option>
                        <option value="snacking"    className="bg-[#121212]">Snacking / Restauration Froide</option>
                        <option value="craft"       className="bg-[#121212]">Artisanat / Créateurs</option>
                        <option value="fashion"     className="bg-[#121212]">Mode / Friperie / Vêtements</option>
                        <option value="vinyls"      className="bg-[#121212]">Vinyles</option>
                        <option value="tattoo"      className="bg-[#121212]">Tatouage / Piercing / Paillettes</option>
                        <option value="associative" className="bg-[#121212]">Engagé / Associatif</option>
                        <option value="partners"    className="bg-[#121212]">Partenaires / Sponsors</option>
                        <option value="service"     className="bg-[#121212]">Services / Bien-être</option>
                        <option value="other"       className="bg-[#121212]">Autre</option>
                      </select>
                      <div className="absolute right-5 top-1/2 -translate-y-1/2 pointer-events-none">
                        <ChevronRight className="w-5 h-5 text-gray-500 rotate-90" />
                      </div>
                    </div>
                  </div>

                  {/* Région */}
                  <div className="md:col-span-2">
                    <label className="block text-sm font-bold text-gray-400 mb-2">Région principale d'activité <C /></label>
                    <div className="relative">
                      <MapPin className="absolute left-5 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-500 pointer-events-none" />
                      <select name="region" value={formData.region} onChange={handleInputChange}
                        className="w-full bg-white/5 border border-white/10 rounded-2xl pl-14 pr-10 py-4 focus:outline-none focus:ring-2 focus:ring-[#ff5a00] transition-all font-medium appearance-none">
                        <option value="" disabled className="bg-[#121212]">Sélectionnez votre région</option>
                        {[
                          'Auvergne-Rhône-Alpes','Bourgogne-Franche-Comté','Bretagne','Centre-Val de Loire',
                          'Corse','Grand Est','Hauts-de-France','Île-de-France','Normandie',
                          'Nouvelle-Aquitaine','Occitanie','Pays de la Loire',
                          "Provence-Alpes-Côte d'Azur",'Outre-Mer','Belgique','Suisse',
                        ].map(r => <option key={r} value={r} className="bg-[#121212]">{r}</option>)}
                      </select>
                      <div className="absolute right-5 top-1/2 -translate-y-1/2 pointer-events-none">
                        <ChevronRight className="w-5 h-5 text-gray-500 rotate-90" />
                      </div>
                    </div>
                  </div>

                  {/* Styles musicaux préférés — critère « thème » du matching */}
                  <div className="md:col-span-2">
                    <label className="block text-sm font-bold text-gray-400 mb-2">
                      Styles musicaux préférés <R />
                      <span className="block text-xs text-gray-600 font-normal mt-1">
                        Sur quels types de festivals aimez-vous exposer ? Améliore vos suggestions « Pour vous ».
                      </span>
                    </label>
                    <div className="flex flex-wrap gap-2 mt-3">
                      {MUSIC_STYLES.map(style => {
                        const active = formData.musicStyles.includes(style.id);
                        return (
                          <button key={style.id} type="button"
                            onClick={() => setFormData(p => ({
                              ...p,
                              musicStyles: active
                                ? p.musicStyles.filter(s => s !== style.id)
                                : [...p.musicStyles, style.id],
                            }))}
                            className={`px-4 py-2.5 rounded-xl border text-xs font-bold transition-all ${
                              active
                                ? 'bg-[#ff5a00] border-[#ff5a00] text-white shadow-lg shadow-[#ff5a00]/20'
                                : 'bg-white/5 border-white/10 text-gray-400 hover:border-white/20'
                            }`}
                          >{style.label}</button>
                        );
                      })}
                    </div>
                  </div>

                  {/* Expérience — fusionné depuis l'ancienne étape dédiée, devenue
                      trop pauvre (2 champs) une fois les noms d'équipe déplacés. */}
                  <div className="md:col-span-2">
                    <label className="block text-sm font-bold text-gray-400 mb-4">Nombre de festivals réalisés</label>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                      {[
                        { id: 'none',  label: "C'est mon premier !" },
                        { id: '1-3',   label: '1 à 3 festivals' },
                        { id: '4-10',  label: '4 à 10 festivals' },
                        { id: '11+',   label: 'Plus de 10 festivals' },
                      ].map((range) => (
                        <button key={range.id} type="button"
                          onClick={() => setFormData(p => ({ ...p, experienceRange: range.id }))}
                          className={`py-4 px-3 rounded-2xl border text-[10px] font-black uppercase tracking-widest transition-all ${
                            formData.experienceRange === range.id
                              ? 'bg-[#ff5a00] border-[#ff5a00] text-white shadow-lg shadow-[#ff5a00]/20'
                              : 'bg-white/5 border-white/10 text-gray-400 hover:border-white/20'
                          }`}
                        >{range.label}</button>
                      ))}
                    </div>
                  </div>

                  <div className="md:col-span-2">
                    <label className="block text-sm font-bold text-gray-400 mb-2">
                      Festivals déjà réalisés <R />
                      <span className="text-gray-500 font-normal text-xs ml-2">renforce votre crédibilité</span>
                    </label>
                    <textarea name="pastFestivals" value={formData.pastFestivals} onChange={handleInputChange} rows={3}
                      placeholder="Ex: Solidays 2024, La Route du Rock 2023, Vieilles Charrues 2022, Festival de Cornouaille..."
                      className="w-full bg-white/5 border border-white/10 rounded-2xl px-5 py-4 focus:outline-none focus:ring-2 focus:ring-yellow-500 transition-all placeholder:text-gray-600 font-medium resize-none" />
                  </div>

                  {/* Fourchette de prix */}
                  <div className="md:col-span-2">
                    <label className="block text-sm font-bold text-gray-400 mb-4">
                      Fourchette de prix <R /> <span className="text-gray-600 font-normal">(panier moyen client)</span>
                    </label>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                      {[
                        { id: 'budget',  label: '€ · < 10€' },
                        { id: 'medium',  label: '€€ · 10–20€' },
                        { id: 'premium', label: '€€€ · 20–40€' },
                        { id: 'luxury',  label: '€€€€ · > 40€' },
                      ].map((range) => (
                        <button key={range.id} type="button"
                          onClick={() => setFormData(p => ({ ...p, priceRange: p.priceRange === range.id ? '' : range.id }))}
                          className={`py-4 px-3 rounded-2xl border text-[11px] font-black uppercase tracking-widest transition-all ${
                            formData.priceRange === range.id
                              ? 'bg-[#ff5a00] border-[#ff5a00] text-white shadow-lg shadow-[#ff5a00]/20'
                              : 'bg-white/5 border-white/10 text-gray-400 hover:border-white/20'
                          }`}
                        >{range.label}</button>
                      ))}
                    </div>
                  </div>

                  {/* Électricité */}
                  <div className="space-y-4">
                    <label className="block text-sm font-bold text-gray-400">Besoin en électricité</label>
                    <ToggleButton
                      active={formData.needsElectricity}
                      onClick={() => toggleBoolean('needsElectricity')}
                      icon={<Zap className={`w-5 h-5 ${formData.needsElectricity ? 'fill-current' : ''}`} />}
                      label={formData.needsElectricity ? "Besoin d'électricité" : 'Aucun besoin électrique'}
                      activeClass="bg-yellow-500/20 border-yellow-500/50 text-yellow-500"
                    />
                    {formData.needsElectricity && (
                      <motion.div initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }}>
                        <label className="block text-sm font-bold text-gray-400 mb-2">Puissance approximative (kW)</label>
                        <input type="number" name="powerKw" value={formData.powerKw || ''} onChange={handleInputChange}
                          step="0.1" placeholder="Ex: 3.5"
                          className="w-full bg-white/5 border border-white/10 rounded-2xl px-5 py-4 focus:outline-none focus:ring-2 focus:ring-yellow-500 transition-all font-medium" />
                      </motion.div>
                    )}
                  </div>

                  {/* Eau & Véhicule */}
                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-bold text-gray-400 mb-2">Accès Eau Potable</label>
                      <ToggleButton
                        active={formData.waterNeeds}
                        onClick={() => toggleBoolean('waterNeeds')}
                        icon={<Droplets className={`w-5 h-5 ${formData.waterNeeds ? 'fill-current' : ''}`} />}
                        label={formData.waterNeeds ? "Besoin d'un raccordement" : "Aucun besoin d'eau"}
                        activeClass="bg-blue-500/20 border-blue-500/50 text-blue-400"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-bold text-gray-400 mb-2">Véhicule sur site</label>
                      <ToggleButton
                        active={formData.hasVehicle}
                        onClick={() => toggleBoolean('hasVehicle')}
                        icon={<Truck className="w-5 h-5" />}
                        label={formData.hasVehicle ? 'Véhicule présent sur place' : 'Pas de véhicule sur stand'}
                        activeClass="bg-purple-500/20 border-purple-500/50 text-purple-400"
                      />
                    </div>
                  </div>

                  {formData.hasVehicle && (
                    <div className="md:col-span-2">
                      <label className="block text-sm font-bold text-gray-400 mb-2">Détails du véhicule (Immatriculation, Modèle)</label>
                      <input type="text" name="vehicleDetails" value={formData.vehicleDetails} onChange={handleInputChange}
                        placeholder="Ex: Renault Master — AA-123-BB"
                        className="w-full bg-white/5 border border-white/10 rounded-2xl px-5 py-4 focus:outline-none focus:ring-2 focus:ring-purple-500 transition-all font-medium" />
                    </div>
                  )}

                  {/* Équipe — regroupe nombre de personnes + noms (auparavant séparés
                      entre l'étape Expérience et l'étape Activité) */}
                  <div className="md:col-span-2 grid grid-cols-1 md:grid-cols-2 gap-8">
                    <div>
                      <label className="block text-sm font-bold text-gray-400 mb-2">Nombre de personnes dans l'équipe</label>
                      <div className="relative">
                        <Users className="absolute left-5 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-500" />
                        <input type="number" name="staffBadges" value={formData.staffBadges || ''} onChange={handleInputChange}
                          placeholder="3"
                          className="w-full bg-white/5 border border-white/10 rounded-2xl pl-14 pr-5 py-4 focus:outline-none focus:ring-2 focus:ring-[#ff5a00] transition-all font-medium text-xl" />
                      </div>
                      <p className="text-[10px] text-gray-600 mt-2 uppercase tracking-widest font-black">Pour les accès et badges nominatifs</p>
                    </div>
                    <div>
                      <label className="block text-sm font-bold text-gray-400 mb-2">Noms / Prénoms de l'équipe</label>
                      <textarea name="staffNames" value={formData.staffNames} onChange={handleInputChange} rows={2}
                        placeholder="Ex: Jean Dupont, Marie Martin..."
                        className="w-full bg-white/5 border border-white/10 rounded-2xl px-5 py-4 focus:outline-none focus:ring-2 focus:ring-[#ff5a00] transition-all placeholder:text-gray-600 font-medium resize-none" />
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-bold text-gray-400 mb-2">Besoins en Mobilier <span className="text-gray-600 font-normal">(optionnel)</span></label>
                    <input type="text" name="furnitureNeeds" value={formData.furnitureNeeds} onChange={handleInputChange}
                      placeholder="Ex: 2 tables, 4 chaises..."
                      className="w-full bg-white/5 border border-white/10 rounded-2xl px-5 py-4 focus:outline-none focus:ring-2 focus:ring-[#ff5a00] transition-all font-medium" />
                  </div>
                </div>

                <NavButtons />
              </motion.div>
            )}

          </AnimatePresence>
        </div>

        {/* Tips bas de page */}
        <div className="mt-12 grid grid-cols-1 md:grid-cols-3 gap-6">
          <div className="p-6 rounded-[2rem] bg-white/5 border border-white/5 hover:bg-white/10 transition-all">
            <div className="w-10 h-10 bg-indigo-500/20 rounded-xl flex items-center justify-center mb-4 border border-indigo-500/30">
              <Globe className="w-5 h-5 text-indigo-400" />
            </div>
            <h4 className="font-bold text-sm mb-2">Visibilité Maximale</h4>
            <p className="text-xs text-gray-500 leading-relaxed">Un profil complet a 4× plus de chances d'être sélectionné par un festival premium.</p>
          </div>
          <div className="p-6 rounded-[2rem] bg-white/5 border border-white/5 hover:bg-white/10 transition-all">
            <div className="w-10 h-10 bg-purple-500/20 rounded-xl flex items-center justify-center mb-4 border border-purple-500/30">
              <Video className="w-5 h-5 text-purple-400" />
            </div>
            <h4 className="font-bold text-sm mb-2">Vidéo = +conversion</h4>
            <p className="text-xs text-gray-500 leading-relaxed">Les exposants avec une vidéo sont sélectionnés 3× plus souvent.</p>
          </div>
          <div className="p-6 rounded-[2rem] bg-white/5 border border-white/5 hover:bg-white/10 transition-all">
            <div className="w-10 h-10 bg-emerald-500/20 rounded-xl flex items-center justify-center mb-4 border border-emerald-500/30">
              <FileCheck className="w-5 h-5 text-emerald-400" />
            </div>
            <h4 className="font-bold text-sm mb-2">Documents 100% optionnels</h4>
            <p className="text-xs text-gray-500 leading-relaxed">Pas sous la main aujourd'hui ? Ajoutez-les plus tard, ils n'ont jamais bloqué votre inscription.</p>
          </div>
        </div>

      </div>
    </div>
  );
}
